import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import DailyChallenge from "../components/DailyChallenge";
import {
  Users,
  TrendingUp,
  Calendar,
  AlertTriangle,
  Flame,
  ArrowRight,
  Plus,
  ChevronRight,
  Quote,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sales motivation quotes
const SALES_QUOTES = [
  { quote: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { quote: "The difference between a successful person and others is not a lack of strength, but a lack of will.", author: "Vince Lombardi" },
  { quote: "Every sale has five basic obstacles: no need, no money, no hurry, no desire, no trust.", author: "Zig Ziglar" },
  { quote: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { quote: "The harder the conflict, the more glorious the triumph.", author: "Thomas Paine" },
  { quote: "Opportunities don't happen. You create them.", author: "Chris Grosser" },
  { quote: "Sales are contingent upon the attitude of the salesman, not the attitude of the prospect.", author: "W. Clement Stone" },
  { quote: "Your attitude, not your aptitude, will determine your altitude.", author: "Zig Ziglar" },
  { quote: "I have never worked a day in my life without selling. If I believe in something, I sell it, and I sell it hard.", author: "Estée Lauder" },
  { quote: "Become the person who would attract the results you seek.", author: "Jim Cathcart" }
];

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuote, setCurrentQuote] = useState(() => 
    SALES_QUOTES[Math.floor(Math.random() * SALES_QUOTES.length)]
  );

  useEffect(() => {
    fetchStats();
    // Rotate quotes every 30 seconds
    const quoteInterval = setInterval(() => {
      setCurrentQuote(SALES_QUOTES[Math.floor(Math.random() * SALES_QUOTES.length)]);
    }, 30000);
    return () => clearInterval(quoteInterval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gba-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome & Quote */}
      <div className="bg-gradient-to-r from-[#3D4D6B] to-[#1E293B] rounded-2xl p-6 border border-slate-700">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
          Hey GB. 👋
        </h1>
        <p className="text-slate-300 text-lg italic">"{stats?.quote}"</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#1E293B] border-slate-700 card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Prospects</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.total_prospects || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-900/50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-slate-700 card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Converted</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.converted || 0}
                </p>
                <p className="text-gba-green text-sm font-medium">
                  {stats?.conversion_rate || 0}% rate
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-900/50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-gba-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-slate-700 card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Due Today</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.due_today || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-900/50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-slate-700 card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Streak</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.streak || 0}
                </p>
                <p className="text-orange-400 text-sm font-medium flex items-center gap-1">
                  <Flame className="w-4 h-4" /> days
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-900/50 flex items-center justify-center">
                <Flame className="w-6 h-6 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Challenge */}
      <DailyChallenge />

      {/* Overdue Follow-ups */}
      {stats?.overdue_count > 0 && (
        <Card className="bg-red-950/30 border-red-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Overdue Follow-ups ({stats.overdue_count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.overdue_prospects?.slice(0, 5).map((prospect) => (
                <Link
                  key={prospect.id}
                  to={`/prospects/${prospect.id}`}
                  className="flex items-center justify-between p-3 bg-[#1E293B] rounded-lg border border-slate-700 hover:border-red-500/50 transition-colors"
                  data-testid={`overdue-prospect-${prospect.id}`}
                >
                  <div>
                    <p className="text-white font-medium">{prospect.name}</p>
                    <p className="text-slate-400 text-sm">{prospect.company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 text-sm">
                      Due: {prospect.follow_up_by}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </Link>
              ))}
            </div>
            {stats.overdue_count > 5 && (
              <Link
                to="/prospects"
                className="block text-center text-gba-green hover:text-gba-green-hover mt-4 font-medium"
              >
                View all overdue prospects →
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/prospects" data-testid="add-prospect-link">
          <Card className="bg-[#1E293B] border-slate-700 hover:border-gba-green transition-colors cursor-pointer group">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gba-green/20 flex items-center justify-center group-hover:bg-gba-green/30 transition-colors">
                  <Plus className="w-7 h-7 text-gba-green" />
                </div>
                <div>
                  <p className="text-white font-semibold text-lg">Add New Prospect</p>
                  <p className="text-slate-400 text-sm">Start tracking a new lead</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-gba-green transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/techniques" data-testid="techniques-link">
          <Card className="bg-[#1E293B] border-slate-700 hover:border-gba-green transition-colors cursor-pointer group">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-900/50 transition-colors">
                  <span className="text-2xl font-heading font-bold text-blue-400">32</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-lg">Follow-up Techniques</p>
                  <p className="text-slate-400 text-sm">Browse all 32 proven methods</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-gba-green transition-colors" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Empty State */}
      {stats?.total_prospects === 0 && (
        <Card className="bg-[#1E293B] border-slate-700 border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-slate-500" />
            </div>
            <h3 className="font-heading text-2xl font-semibold text-white mb-2">
              No Prospects Yet
            </h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Start adding prospects to track your follow-ups and close more deals. Remember: 80% of sales close after 5+ follow-ups!
            </p>
            <Link to="/prospects">
              <Button className="bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 px-8 text-lg uppercase tracking-wide shadow-glow">
                <Plus className="w-5 h-5 mr-2" />
                Add Your First Prospect
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Motivation Quote - Fixed Bottom Right */}
      <div className="fixed bottom-6 right-6 max-w-sm z-40 hidden lg:block">
        <div className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-xl p-5 border border-slate-700/50 shadow-2xl backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gba-green/20 flex items-center justify-center flex-shrink-0 mt-1">
              <Sparkles className="w-4 h-4 text-gba-green" />
            </div>
            <div>
              <p className="text-slate-300 text-sm leading-relaxed italic">
                "{currentQuote.quote}"
              </p>
              <p className="text-gba-green text-xs mt-2 font-medium">
                — {currentQuote.author}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
